/**
 * Purpose: Vehicle related selectors.
 */

import type { PaymentWithDealerIdRequest } from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import type { ExtendedFeatureToggles } from '../types/commonTypes';
import { getOfferNode } from './helpers';

export const getDealerNode = (state: OfferReduxBaseState): PaymentWithDealerIdRequest => {
    return state.dealer || {};
};

export const getPartnerId = (state: OfferReduxBaseState): string | undefined => {
    const dealer = getDealerNode(state);
    return dealer.partnerId;
};

export const getPartnerDealerId = (state: OfferReduxBaseState): string | number | undefined => {
    const dealer = getDealerNode(state);
    return dealer.partnerDealerId;
};

/**
 * Reads `dealer.autoSelectProgramByDefault` and normalizes the tri-state
 * (`true | false | undefined`) to a strict boolean. Undefined is treated as
 * `false` per US1972679: a dealer with no explicit setting is opted out.
 */
export const isAutoSelectProgramByDefaultEnabled = (state: OfferReduxBaseState): boolean => {
    const dealer = getDealerNode(state);
    return Boolean((dealer as { autoSelectProgramByDefault?: boolean }).autoSelectProgramByDefault);
};

export const isDealerAutoSelectSettingBypassed = (state: OfferReduxBaseState): boolean =>
    (getOfferNode(state)?.featureToggles as ExtendedFeatureToggles | undefined)?.US2161518_Bypass_Dealer_Auto_Select_Setting ===
    true;

/**
 * Raw tri-state read of `dealer.autoSelectProgramByDefault` (`true | false | undefined`).
 * Use this where `undefined` (no admin configuration) must be distinguished from `false`.
 */
export const getAutoSelectProgramByDefault = (state: OfferReduxBaseState): boolean | undefined => {
    if (isDealerAutoSelectSettingBypassed(state)) return undefined;
    const dealer = getDealerNode(state);
    return (dealer as { autoSelectProgramByDefault?: boolean }).autoSelectProgramByDefault;
};
